import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import {useCallback, useContext, useEffect, useMemo, useRef} from 'react';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {
  ORDER_BY_FILTER,
  ORDER_FILTER,
  SEARCH_FILTERS,
  SEARCH_TYPE,
} from '@/src/app/constants/search';
import useSearchActions from '@/src/app/store/search/useSearchActions';
import useUserActions from '@/src/app/store/user/useUserActions';
import useEmploymentTypes from '@/src/hooks/useEmploymentTypes';
import useLoading from '@/src/hooks/useLoading';
import useTargetClinic from '@/src/profile/clinicTarget/hooks/useTargetClinic';
import RouteContext from '@/src/routes/utils/RouteContext';
import FiltersLayout from '@/src/Search/components/FiltersLayout';
import SearchTabsNavigation from '@/src/Search/components/SearchTabsNavigation';
import {getIsQueryParamsExist} from '@/src/Search/containers/SearchContainer';
import SearchFieldContainer from '@/src/Search/containers/SearchFieldContainer';
import SearchResults from '@/src/Search/containers/SearchResults';
import css from '@/src/Search/styles/SearchContainer.module.css';
import {checkExsistingFilter, getData} from '@/src/Search/utils/index';
import {useSearchFilters} from '@/src/Search/utils/useSearchFilters';
import TextLocal from '@/src/textLocal/containers/TextLocal';
import {LoadingWrapper} from '@/src/ui';

interface ClinicVacanciesProps {
  isAllVacanciesPage?: boolean;
  withCommonSearchNavigation?: boolean;
}

const ClinicVacancies = ({
  isAllVacanciesPage,
  withCommonSearchNavigation,
}: ClinicVacanciesProps) => {
  const {query, locale, isReady: isRouterReady, push} = useRouter();
  const {isLoading, setLoading} = useLoading();
  const areInitialFiltersSetRef = useRef(false);
  // @ts-ignore
  const {setQueryFilters} = useSearchFilters();
  const isQueryParamsExist = getIsQueryParamsExist(query);
  const {setResultsDataAction} = useSearchActions();
  const {user} = useUserActions();
  const {t} = useTranslation(TextLocal.LOCALE_SOURCE.COMMON);
  const {requestTargetClinicById, clinic} = useTargetClinic();
  const {employmentTypes} = useEmploymentTypes();
  const isTargetClinicLoadingRef = useRef(false);
  const controllerRef = useRef();

  const {
    // @ts-ignore
    SEARCH_COURSES,
    // @ts-ignore
    SEARCH_USERS,
    // @ts-ignore
    SEARCH_UNITED_ROUTE,
    // @ts-ignore
    SEARCH_CLINICS,
    // @ts-ignore
    SEARCH_VACANCIES,
    // @ts-ignore
    SEARCH_POSTS,
  } = useContext(RouteContext);

  useEffect(() => {
    if (
      !query[SEARCH_FILTERS.CLINIC_ID] ||
      isTargetClinicLoadingRef.current === true
    ) {
      return null;
    }

    isTargetClinicLoadingRef.current = true;

    requestTargetClinicById(Number(query[SEARCH_FILTERS.CLINIC_ID]));
  }, [query, clinic]);

  const initialSearchState = useMemo(() => {
    return {
      [SEARCH_FILTERS.OFFSET]: query[SEARCH_FILTERS.OFFSET] || '0',
      [SEARCH_FILTERS.ORDER]: query[SEARCH_FILTERS.ORDER] || ORDER_FILTER.DESC,
      [SEARCH_FILTERS.LANGUAGE]: locale,
      [SEARCH_FILTERS.ORDER_BY]: ORDER_BY_FILTER.APPROVED_AT,
      [SEARCH_FILTERS.SEARCH]: query[SEARCH_FILTERS.SEARCH] || '',
    };
  }, [query, locale]);

  useEffect(() => {
    if (isRouterReady && !areInitialFiltersSetRef.current) {
      setQueryFilters({
        ...initialSearchState,
        ...query,
      });

      areInitialFiltersSetRef.current = true;
    }
  }, [isRouterReady, query]);

  useEffect(() => {
    if (controllerRef.current) {
      // @ts-ignore
      controllerRef.current?.abort();
    }

    if (locale === query[SEARCH_FILTERS.LANGUAGE]) {
      // @ts-ignore
      controllerRef.current = new AbortController();

      fetchVacancies(query, query.slug);
    } else {
      setQueryFilters({
        ...query,
        [SEARCH_FILTERS.LANGUAGE]: locale,
      });
    }

    return () => {
      if (controllerRef.current) {
        // @ts-ignore
        controllerRef.current?.abort();
      }

      setResultsDataAction({});
    };
  }, [
    query.slug,
    locale,
    query.search,
    JSON.stringify(checkExsistingFilter(query)),
  ]);

  const fetchVacancies = useCallback(
    async (params, slug) => {
      setLoading(true);

      const {status, responseBody} = await getData(
        params,
        slug,
        null,
        null,
        // @ts-ignore
        controllerRef.current?.signal
      );

      setLoading(false);

      if (status === RESPONSE_STATUSES.SUCCESS) {
        setResultsDataAction({
          data: responseBody.data,
          totalAmount: responseBody.total_amount,
        });
      }
    },
    [query, controllerRef.current]
  );

  const handleTabClick = useCallback(
    (newSearchType) => {
      let newPathname = null;

      setResultsDataAction({});

      switch (newSearchType) {
        case SEARCH_TYPE.USERS: {
          newPathname = SEARCH_USERS.getLink();
          break;
        }
        case SEARCH_TYPE.COURSES: {
          newPathname = SEARCH_COURSES.getLink();
          break;
        }
        case SEARCH_TYPE.CLINICS: {
          newPathname = SEARCH_CLINICS.getLink();
          break;
        }
        case SEARCH_TYPE.UNITED: {
          newPathname = SEARCH_UNITED_ROUTE.getLink();
          break;
        }
        case SEARCH_TYPE.POSTS: {
          newPathname = SEARCH_POSTS.getLink();
          break;
        }
        case SEARCH_TYPE.VACANCIES: {
          newPathname = SEARCH_VACANCIES.getLink();
        }
      }

      push({
        pathname: newPathname,
        query: {
          [SEARCH_FILTERS.OFFSET]: '0',
          [SEARCH_FILTERS.ORDER]: ORDER_FILTER.DESC,
          [SEARCH_FILTERS.LANGUAGE]: locale,
          [SEARCH_FILTERS.SEARCH]: query[SEARCH_FILTERS.SEARCH] || '',
        },
      });
    },
    [query, locale]
  );

  return (
    <>
      <SearchFieldContainer
        withBottomMargin
        searchLabel={
          withCommonSearchNavigation || !clinic?.data?.organization_name
            ? t('search.search-results-by-jobs')
            : t('job-portal.all-positions-from', {
                clinicName: clinic?.data?.organization_name,
              })
        }
        withLabel={!isAllVacanciesPage || withCommonSearchNavigation}
      />
      {withCommonSearchNavigation && (
        <SearchTabsNavigation
          onTabClick={handleTabClick}
          // @ts-ignore
          activeSearchType={query.slug}
        />
      )}
      <LoadingWrapper
        isLoading={
          isLoading ||
          (clinic.isLoading && !isAllVacanciesPage) ||
          !employmentTypes.length
        }
      >
        {isQueryParamsExist && (
          <div className={css.contentWrapper}>
            <div className={css.content}>
              <FiltersLayout />
              <section className={css.section}>
                <SearchResults
                  currentUserId={user?.user?.id}
                  clinicData={clinic.data}
                  employmentTypes={employmentTypes}
                  onSuccessfulApplicationCallback={() =>
                    fetchVacancies(query, query.slug)
                  }
                />
              </section>
            </div>
          </div>
        )}
      </LoadingWrapper>
    </>
  );
};

export default ClinicVacancies;
